import type { IPOCalendarResponse, WatchQueueItem } from '../types/ipo';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

class IPOService {
  private getToken(): string | null {
    return localStorage.getItem('token');
  }

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${API_URL}${endpoint}`, {
      headers,
      ...options,
    });

    if (response.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
      throw new Error('Session expired');
    }

    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(err.detail || 'Request failed');
    }

    // 204 No Content — nothing to parse
    if (response.status === 204) {
      return undefined as unknown as T;
    }

    return response.json();
  }

  async getCalendar(): Promise<IPOCalendarResponse> {
    return this.request<IPOCalendarResponse>('/api/v1/ipo/calendar');
  }

  async getWatchQueue(): Promise<WatchQueueItem[]> {
    return this.request<WatchQueueItem[]>('/api/v1/ipo/watch-queue');
  }

  async addToWatchQueue(ipoCalendarId: number): Promise<WatchQueueItem> {
    return this.request<WatchQueueItem>('/api/v1/ipo/watch-queue', {
      method: 'POST',
      body: JSON.stringify({ ipo_calendar_id: ipoCalendarId }),
    });
  }

  async removeFromWatchQueue(itemId: number): Promise<void> {
    return this.request<void>(`/api/v1/ipo/watch-queue/${itemId}`, {
      method: 'DELETE',
    });
  }
}

export const ipoService = new IPOService();
